import React, { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Drawer,
  DrawerContent,
  DrawerOverlay,
  Flex,
  IconButton,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react'
import { AnimatePresence } from 'framer-motion'
import { FiChevronRight } from 'react-icons/fi'
import NavigationBar from './NavigationBar'
import Sidebar from './Sidebar'
import ImpersonationBanner from './ImpersonationBanner'
import ChatbotWidget from '../Chatbot/ChatbotWidget'
import CommandPalette from '../CommandPalette'
import UploadPanel from '../Upload/UploadPanel'
import MotionBox from '../Common/MotionBox'
import { slideInLeftVariants } from '@/theme/animations'
import { useCommandPalette } from '@/hooks/useKeyboardShortcut'
import { useAppTranslation } from '@/contexts/LanguageContext'
import { useMatch } from 'react-router-dom'
import { useAppSelector } from '@/store'
import { selectIsUploadPanelVisible } from '@/store/selectors'

interface DashboardLayoutProps {
  children: React.ReactNode
}

// Chakra's `lg` breakpoint is 62em = 992px.
const LG_BREAKPOINT_PX = 992

const getIsMobileSync = () => {
  if (typeof window === 'undefined') return false
  return window.innerWidth < LG_BREAKPOINT_PX
}

// Whether the desktop sidebar is expanded survives reloads; the mobile drawer
// deliberately does not (it always starts closed, over the content).
const SIDEBAR_STORAGE_KEY = 'sidebar-open'

const readStoredSidebarOpen = () => {
  if (typeof window === 'undefined') return true

  try {
    const stored = window.localStorage.getItem(SIDEBAR_STORAGE_KEY)
    return stored === null ? true : stored === 'true'
  } catch {
    // Storage unavailable (Safari private mode throws) — fall back to open.
    return true
  }
}

export default function DashboardLayout({ children }: DashboardLayoutProps) {
  // Detect mobile synchronously on the first render so the initial layout
  // (and the sidebar's default open state) is correct from frame zero.
  // Chakra's useBreakpointValue returns the `base` value on the first render
  // and resolves asynchronously, which caused the sidebar to mount in its
  // mobile full-screen layout and then reflow on desktop.
  const [isMobile, setIsMobile] = useState(getIsMobileSync)
  const { t } = useAppTranslation(['common'])

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${LG_BREAKPOINT_PX - 1}px)`)
    const handler = (event: MediaQueryListEvent) => setIsMobile(event.matches)
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [])

  const dashboardMatch = useMatch({ path: '/', end: true })
  const dashboardExactMatch = useMatch({ path: '/dashboard', end: true })
  const isSearchPage = useMatch('/search')
  const isPreviewPage = useMatch('/preview/:fileId')

  // Two independent flags rather than one: the desktop preference must survive
  // a trip through the mobile breakpoint, and the mobile drawer must never
  // inherit a "was open on desktop" state and cover the whole app on load.
  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(readStoredSidebarOpen)
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false)

  useEffect(() => {
    try {
      window.localStorage.setItem(SIDEBAR_STORAGE_KEY, String(isDesktopSidebarOpen))
    } catch {
      // Quota exceeded or storage disabled — the preference just won't persist.
    }
  }, [isDesktopSidebarOpen])

  // Leaving mobile unmounts the drawer; drop its state so it can't reopen
  // behind the desktop sidebar on the way back.
  useEffect(() => {
    if (!isMobile) setIsMobileSidebarOpen(false)
  }, [isMobile])

  const isSidebarOpen = isMobile ? isMobileSidebarOpen : isDesktopSidebarOpen

  const handleSidebarToggle = useCallback(() => {
    if (isMobile) setIsMobileSidebarOpen((open) => !open)
    else setIsDesktopSidebarOpen((open) => !open)
  }, [isMobile])

  const handleMobileSidebarClose = useCallback(() => setIsMobileSidebarOpen(false), [])

  const {
    isOpen: isCommandPaletteOpen,
    onOpen: onCommandPaletteOpen,
    onClose: onCommandPaletteClose,
  } = useDisclosure()

  // Register Cmd+K / Ctrl+K shortcut to open command palette
  useCommandPalette(onCommandPaletteOpen)

  const isDashboard = !!dashboardMatch || !!dashboardExactMatch
  const showChatbot = isDashboard
  const isUploadPanelVisible = useAppSelector(selectIsUploadPanelVisible)

  // Search and preview render their own top bars instead of the NavigationBar,
  // so the hamburger is out of reach there — pin an edge affordance instead.
  const showNavigationBar = !isSearchPage && !isPreviewPage
  const showEdgeToggle = !showNavigationBar && !isSidebarOpen

  return (
    <Box h="100vh" overflow="hidden" bg="gray.100" display="flex" flexDirection="column">
      <ImpersonationBanner />
      <Flex flex="1" overflow="hidden">
        {/* Desktop sidebar — unmounted when hidden so the row reclaims the space. */}
        {!isMobile && (
          <AnimatePresence initial={false}>
            {isDesktopSidebarOpen && (
              <MotionBox
                initial={slideInLeftVariants.initial}
                animate={slideInLeftVariants.animate}
                exit={slideInLeftVariants.exit}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                position="relative"
                height="auto"
                bg="transparent"
                flexShrink={0}
              >
                <Sidebar isOpen isMobile={false} />
              </MotionBox>
            )}
          </AnimatePresence>
        )}

        {/* Main Content Area */}
        <Box flex="1" display="flex" flexDirection="column" overflow="hidden" bg="white" minW={0}>
          {/* Navigation Bar — hidden on preview, which has its own top bar */}
          {showNavigationBar && (
            <NavigationBar onSidebarToggle={handleSidebarToggle} isSidebarOpen={isSidebarOpen} />
          )}

          {/* Main Content — the preview page manages its own scroll regions,
              so letting this box scroll too would produce a second scrollbar. */}
          <Box flex="1" overflow={isPreviewPage ? 'hidden' : 'auto'} bg="bg-gray-50">
            {children}
          </Box>

          {/* Chatbot Widget */}
          {/* Hidden rather than unmounted so the conversation survives; the
              upload panel occupies the same corner while it is on screen. */}
          {showChatbot && (
            <Box display={isUploadPanelVisible ? 'none' : 'block'}>
              <ChatbotWidget />
            </Box>
          )}
        </Box>
      </Flex>

      {/* Mobile sidebar — an overlay so it never pushes the app off screen.
          The Drawer supplies the backdrop, Escape handling and focus trap;
          the close button lives inside Sidebar itself. */}
      {isMobile && (
        <Drawer
          placement="left"
          isOpen={isMobileSidebarOpen}
          onClose={handleMobileSidebarClose}
          autoFocus={false}
        >
          <DrawerOverlay />
          <DrawerContent maxW="320px" p={0} bg="gray.100">
            <Sidebar isOpen onClose={handleMobileSidebarClose} isMobile />
          </DrawerContent>
        </Drawer>
      )}

      {showEdgeToggle && (
        <Tooltip label={t('buttons.showSidebar', { ns: 'common' })} placement="right">
          <IconButton
            aria-label={t('buttons.showSidebar', { ns: 'common' })}
            icon={<FiChevronRight />}
            size="sm"
            position="fixed"
            left={0}
            top="50%"
            transform="translateY(-50%)"
            zIndex={1001}
            bg="white"
            color="gray.600"
            borderWidth="1px"
            borderLeftWidth={0}
            borderColor="gray.200"
            borderRadius="0 var(--chakra-radii-md) var(--chakra-radii-md) 0"
            boxShadow="md"
            _hover={{ bg: 'gray.100', color: 'gray.800' }}
            onClick={handleSidebarToggle}
          />
        </Tooltip>
      )}

      {/* Command Palette */}
      <CommandPalette isOpen={isCommandPaletteOpen} onClose={onCommandPaletteClose} />

      {/* Mounted at the app root so it is not clipped by the content box and
          keeps reporting after navigating away from the dashboard. */}
      <UploadPanel />
    </Box>
  )
}
